import {
  DownloadOutlined,
  MoreOutlined,
  PlusOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import ExternalIcon from 'assets/icons/external.svg?react';
import {
  Button,
  Col,
  Dropdown,
  Input,
  Row,
  Select,
  Table,
  notification,
} from 'antd';
import CurrentFilterBadges from 'components/WorkOrder/CurrentFilterBadges';
import { renderStatusFilterDropdown } from 'components/WorkOrder/WorkOrderRenderUtils';
import _, { get } from 'lodash';
import moment from 'moment-timezone';
import { PropTypes } from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { currencyOrNumber } from 'shared/utils';
import {
  AppStore,
  CarStore,
  CurrentUserStore,
  ShopStore,
  UserStore,
  WorkOrderStore,
} from 'stores';
import { CarObject, Logger } from 'stores/Classes';
import {
  WorkOrderFilterOptions,
  // WorkOrderRepairPriorityEnum,
  WorkOrderStatusEnum,
} from 'stores/Classes/WorkOrderObject';
import { EventBus } from 'stores/EventBus';
import styled from 'styled-components';

const TableContainer = styled(Table)`
  .ant-table-pagination.ant-pagination {
    text-align: center;
    float: none;
    display: flex;
    justify-content: center;
  }
  .ant-pagination-options {
    display: none;
  }
`;

const Wrapper = styled.div``;

const DropdownButtonContainer = styled.div`
  .more-button-dropdown {
    min-width: 32px;
    min-height: 32px;
    border-radius: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 2px;

    i {
      font-size: 1.3rem;
      font-weight: bold;
      color: #000;
    }
  }
`;

const FlexColJustifyEnd = styled(Col)`
  display: flex;
  justify-content: flex-end;
`;

const { Search } = Input;

class AssetWorkOrders extends React.Component {
  static propTypes = {
    car: PropTypes.instanceOf(CarObject),
    shopId: PropTypes.number,
    history: PropTypes.object.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      workOrders: [],
      currentSearch: '',
      statusFiltersApplied: [],
      filteredInfo: {
        status: [],
      },
      filterByStatusDropdownVisible: false,
      shopUsers: [],
      sorterData: {
        columnKey: 'started_at',
        order: 'descend',
      },
      shopWorkOrderStatuses: {},
    };

    this.columns = this.getColumns();
  }

  get currentFiltersApplied() {
    return [...this.state.statusFiltersApplied];
  }

  componentDidMount() {
    this.init();
  }

  async componentDidUpdate(prevProps, prevState) {
    let { currentSearch } = this.state;
    if (currentSearch !== prevState.currentSearch) {
      this.handleSearchChange();
    }
  }

  searchDebouncer = null;

  handleSearchChange = () => {
    // Clears running timer and starts a new one each time the user types
    clearTimeout(this.searchDebouncer);
    this.searchDebouncer = setTimeout(() => {
      this.reloadWorkOrders();
    }, 500);
  };

  init = async () => {
    await this.loadShopUsers();
    await this.loadShopWorkOrderStatuses();
    this.reloadWorkOrders();
  };

  loadShopUsers = async () => {
    try {
      this.setState({ loading: true });
      const users = await UserStore.fetchShopUsers(this.props.shopId);
      await this.setState({ shopUsers: users || [] });
    } catch (error) {
      // if AbortError, ignore
      if (error.name === 'AbortError') {
        return;
      }
      AppStore.addError('Failed to load shop users. Please try again later.');
    } finally {
      this.setState({ loading: false });
    }
  };

  loadShopWorkOrderStatuses = async () => {
    const shopId = ShopStore.currentShop.id;
    try {
      this.setState({ isLoadingStatuses: true });
      const shopWorkOrderStatuses =
        await WorkOrderStore.getShopWorkOrderStatuses(shopId);

      const formattedStatuses = _.chain(shopWorkOrderStatuses)
        .get('data', [])
        .reduce((acc, item) => {
          acc[item.key] = item.description;
          return acc;
        }, {})
        .value();

      this.setState({
        shopWorkOrderStatuses: formattedStatuses,
      });
    } catch (error) {
      // if AbortError, ignore
      if (error.name === 'AbortError') {
        return;
      }
      Logger.error(error);
    } finally {
      this.setState({ isLoadingStatuses: false });
    }
  };

  reloadWorkOrders = async () => {
    try {
      this.setState({ loading: true });
      const filters = {
        id_shop: this.props.shopId,
        carIds: this.props.car.id,
        limit: 999,
      };
      if (this.state.statusFiltersApplied.length > 0) {
        filters.status = this.state.statusFiltersApplied
          .map((filter) => filter.key)
          .join(',');
      }
      if (this.state.currentSearch !== '') {
        filters.search = this.state.currentSearch;
      }
      const sortColumn = get(this.state.sorterData, 'columnKey');
      const sortOrder = get(this.state.sorterData, 'order');
      if (sortColumn) {
        filters.sort = `${sortOrder === 'descend' ? '-' : ''}${sortColumn}`;
      }
      const { data: workOrders } =
        await WorkOrderStore.fetchWorkOrders(filters);
      this.setState({ workOrders });
    } catch (error) {
      // if AbortError, ignore
      if (error.name === 'AbortError') {
        return;
      }
      AppStore.addError(error.message || 'Failed to load work orders');
    } finally {
      this.setState({ loading: false });
    }
  };

  handleMenuClick = async ({ key, domEvent }, row) => {
    domEvent.stopPropagation();
    domEvent.preventDefault();

    if (key === '1') {
      try {
        AppStore.addLoading('Getting Work Order Report');
        await WorkOrderStore.printWorkOrder(row.id);
      } catch (error) {
        AppStore.addError(
          'Unknown error to generate the Work Order. Please try again later'
        );
      } finally {
        AppStore.removeLoading('Getting Work Order Report');
      }
    } else if (key === '2') {
      try {
        AppStore.addLoading('Getting Work Order Report');
        await WorkOrderStore.downloadWorkOrder(row.id);
      } catch (error) {
        AppStore.addError(
          'Unknown error to generate the Work Order. Please try again later'
        );
      } finally {
        AppStore.removeLoading('Getting Work Order Report');
      }
    }
  };

  handleStatusChange = async (newStatus, row) => {
    try {
      const response = await WorkOrderStore.updateWorkOrderOnCustomEndpoint({
        id: row.id,
        status: newStatus,
      });

      // Check if we need to Resolve a DVIR
      if (
        newStatus === 'resolved' &&
        row.type === 'driver_identified' &&
        row.work_order_dvirs.length > 0
      ) {
        if (!CurrentUserStore.user.email) {
          throw new Error('An user is required to resolve the DVIR');
        }

        const samsaraUsers = await CarStore.fetchSamsaraUsers(
          ShopStore.currentShop.id
        );
        // Select the current user email if it's in the list
        const currentUserEmail = CurrentUserStore.user.email;
        const samsaraUserResolving = samsaraUsers.find(
          (user) =>
            String(user.email).toLowerCase() ===
            String(currentUserEmail).toLowerCase()
        );

        let defects = [];
        let dvirId = row.work_order_dvirs[0].dvir_id;
        row.work_order_dvirs.forEach((dvirDefects) => {
          defects.push({ id: dvirDefects.defect_id.toString() });
        });

        if (dvirId && defects.length > 0) {
          const defectsResolved = await CarStore.resolveDVIRDefects({
            shopId: ShopStore.currentShop.id,
            dvirId: dvirId,
            defects: defects,
            mechanicNotes: 'Resolved by Pitstop',
            resolvedBySamsaraUserId:
              samsaraUserResolving?.id || currentUserEmail,
          });
          AppStore.addNotification(
            `${defectsResolved.length} defects resolved successfully`
          );
        }
      }

      notification.success({
        message: 'Status updated',
        key: `statusUpdated${moment().format('x')}`,
        description: <>The work order status was updated.</>,
      });
      return response;
    } catch (error) {
      let friendlyMsg =
        'We couldn\'t update the Work Order status at this moment, please try again later';
      return notification.error({
        message: 'Oops!',
        key: `statusUpdateError${moment().format('x')}`,
        description: <>{friendlyMsg}</>,
      });
    }
  };

  onChangeCurrentFilters = () => {
    // update table filter based on the filters selected
    const filters = {
      status: [],
    };
    this.state.statusFiltersApplied.forEach((filter) => {
      filters['status'].push(filter.key);
    });
    this.setState({ filteredInfo: filters }, () => {
      this.reloadWorkOrders();
    });
  };

  getColumns = () => {
    const statusOptions = Object.keys(this.state.shopWorkOrderStatuses || {})
      .length
      ? Object.keys(this.state.shopWorkOrderStatuses).map((key) => ({
          text: this.state.shopWorkOrderStatuses[key],
          value: key,
        }))
      : WorkOrderFilterOptions;

    return [
      {
        dataIndex: 'invoice_number',
        editable: false,
        title: 'Order #',
        required: true,
        render: (wo, row) => {
          return (
            <a
              style={{ color: '#1890ff', cursor: 'pointer' }}
              onClick={(e) => {
                e.preventDefault();
                EventBus.$emit('dashboardWrapper/OPEN_SLIDE_OUT', {
                  type: 'work-order',
                  data: row,
                  title: `Work Order #${wo}`,
                  selectedCarId: row.id_car,
                });
              }}
            >
              {`#${wo}`}
              <ExternalIcon
                style={{ width: '12px', height: '12px', marginLeft: '4px' }}
              />
            </a>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        sorter: true,
        filters: statusOptions,
        filteredValue: this.state.filteredInfo.status,
        render: (status, row) => {
          const source = Object.keys(this.state.shopWorkOrderStatuses).length
            ? this.state.shopWorkOrderStatuses
            : WorkOrderStatusEnum;

          const options = Object.keys(source)
            .map((workOrderStatusKey) => ({
              key: workOrderStatusKey,
              value: source[workOrderStatusKey],
            }))
            .map((workOrderStatus) => {
              return (
                <Select.Option
                  key={workOrderStatus.key}
                  value={workOrderStatus.key}
                >
                  {workOrderStatus.value}
                </Select.Option>
              );
            });
          return (
            <Select
              defaultValue={status}
              style={{ width: 180 }}
              onChange={(evt) => this.handleStatusChange(evt, row)}
              onClick={(evt) => evt.stopPropagation()}
            >
              {options}
            </Select>
          );
        },
      },
      {
        title: 'Created At',
        dataIndex: 'started_at',
        key: 'started_at',
        defaultSortOrder: 'descend',
        sorter: true,
        render: (text) => {
          return text ? moment(text).format('MM/DD/YYYY') : 'Missing Date';
        },
      },
      {
        title: 'Assigned To',
        dataIndex: 'assigned_to',
        key: 'assigned_to',
        sorter: true,
        render: (userId) => {
          const user = this.state.shopUsers.find((u) => u.userId === +userId);
          return user
            ? `${user.firstName} ${user.lastName}`.trim() || user.email
            : 'Unassigned';
        },
      },
      {
        title: 'Total Cost',
        dataIndex: 'totalCost',
        key: 'totalCost',
        render: (text, workOrder) => {
          return currencyOrNumber(_.get(workOrder, 'costsTotal', 0));
        },
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        key: 'actions',
        render: (_, row) => {
          return (
            <Dropdown
              menu={{
                onClick: (evt) => this.handleMenuClick(evt, row),
                items: [
                  {
                    key: '1',
                    icon: <PrinterOutlined />,
                    label: 'Print work order',
                  },
                  {
                    key: '2',
                    icon: <DownloadOutlined />,
                    label: 'Download work order',
                  },
                ],
              }}
            >
              <DropdownButtonContainer>
                <Button
                  shape="circle"
                  size="small"
                  className="more-button-dropdown"
                >
                  <MoreOutlined />
                </Button>
              </DropdownButtonContainer>
            </Dropdown>
          );
        },
      },
    ];
  };

  render() {
    const columns = this.getColumns();

    return (
      <Wrapper>
        <Row gutter={[16, 16]}>
          <Col span={6}>
            <Search
              placeholder="Search by Order #"
              onChange={(event) => {
                this.setState({
                  currentSearch: event.target.value,
                });
              }}
            />
          </Col>
          <FlexColJustifyEnd span={18}>
            <Button
              onClick={() => {
                this.props.history.push(
                  `/work-order/add?assetId=${this.props.car.id}`
                );
              }}
              type="primary"
            >
              <PlusOutlined />
              Add New
            </Button>
          </FlexColJustifyEnd>
        </Row>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <CurrentFilterBadges
              currentFiltersApplied={this.currentFiltersApplied}
              onClearAllFilters={() => {
                this.setState(
                  {
                    statusFiltersApplied: [],
                    filteredInfo: { status: [] },
                  },
                  () => {
                    this.reloadWorkOrders();
                  }
                );
              }}
              onCloseTag={(filter) => {
                const updatedFiltersApplied =
                  this.state.statusFiltersApplied.filter(
                    (f) => f.key !== filter.key
                  );

                const updatedFilterInfo = {
                  ...this.state.filteredInfo,
                  status: updatedFiltersApplied.map((f) => f.key),
                };

                this.setState(
                  {
                    statusFiltersApplied: updatedFiltersApplied,
                    filteredInfo: updatedFilterInfo,
                  },
                  () => {
                    this.reloadWorkOrders();
                  }
                );
              }}
            />
          </Col>
        </Row>
        <TableContainer
          columns={columns}
          dataSource={this.state.workOrders}
          loading={this.state.loading}
          rowKey="id"
          onChange={(pagination, filters, sorter) => {
            // Update state with the new filters
            const statusFiltersApplied = filters.status
              ? filters.status.map((key) => {
                  const source = this.state.shopWorkOrderStatuses || {};
                  const statusOptions = Object.keys(
                    this.state.shopWorkOrderStatuses || {}
                  ).length
                    ? Object.keys(this.state.shopWorkOrderStatuses).map(
                        (key) => ({
                          text: this.state.shopWorkOrderStatuses[key],
                          value: key,
                        })
                      )
                    : WorkOrderFilterOptions;
                  const statusOption = statusOptions.find(
                    (opt) => opt.value === key
                  );

                  return {
                    key,
                    label: source[key] || statusOption?.text || key,
                  };
                })
              : [];

            this.setState(
              {
                filteredInfo: filters,
                statusFiltersApplied,
                sorterData: sorter,
              },
              () => {
                this.reloadWorkOrders();
              }
            );
          }}
        />
      </Wrapper>
    );
  }
}

export default withRouter(AssetWorkOrders);
